import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import WheelComponent from './components/WheelComponent';
import WinningModal from './components/WinningModal';
import WinningList from './components/WinningList';
import ConfigModal from './components/ConfigModal';

const AppWrapper = styled.div`
  min-height: 100vh;
  background: url('/images/background.gif') no-repeat center center fixed;
  background-size: cover;
  position: relative;
  display: flex;
`;

const Container = styled.div`
  display: flex;
  gap: 1rem;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  justify-content: center; 
  border-radius: 12px; 
  align-items: anchor-center;
`;

const ConfigButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 1rem;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  transition: transform 0.3s;

  &:hover {
    transform: scale(1.1);
  }
`;

function App() {
  const [mustSpin, setMustSpin] = useState(false);
  const [segments, setSegments] = useState([
    { option: 'Phần thưởng 1' },
    { option: 'Phần thưởng 2' },
    { option: 'Phần thưởng 3' },
    { option: 'Phần thưởng 4' },
    { option: 'Phần thưởng 5' },
  ]);
  const [prizeNumber, setPrizeNumber] = useState(0);
  /** Giải thưởng vừa thắng (lưu trước khi cập nhật segments để modal/history không đọc sai) */
  const [lastWonPrize, setLastWonPrize] = useState('');
  const [lastRemainingCount, setLastRemainingCount] = useState(0);
  const [showWinningModal, setShowWinningModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [winningHistory, setWinningHistory] = useState([]);

  // Load winning history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('winningHistory');
    if (savedHistory) {
      setWinningHistory(JSON.parse(savedHistory));
    }
  }, []);

  const handleSpinClick = () => {
    if (!mustSpin) {
      const newPrizeNumber = Math.floor(Math.random() * segments.length);
      setPrizeNumber(newPrizeNumber);
      setMustSpin(true);
    }
  };

  const handleStopSpinning = () => {
    // Quan trọng: đọc giải thắng và số còn lại TRƯỚC khi setSegments (tránh đọc sai do batch update)
    const winningOption = segments[prizeNumber].option;
    const remainingAfterWin = segments.length - 1;

    setMustSpin(false);
    setLastWonPrize(winningOption);
    setLastRemainingCount(remainingAfterWin);
    setShowWinningModal(true);

    // Add to history (dùng winningOption đã lưu)
    const newHistory = [{
      prize: winningOption,
      timestamp: new Date().toLocaleString()
    }, ...winningHistory];

    setWinningHistory(newHistory);
    localStorage.setItem('winningHistory', JSON.stringify(newHistory));

    // Xóa đúng phần thưởng theo index thật (prizeNumber)
    const newSegments = segments.filter((_, index) => index !== prizeNumber);

    if (newSegments.length === 0) {
      alert('Đã hết phần thưởng! Vòng quay sẽ được reset.');
      setSegments([
        { option: 'Phần thưởng 1' },
        { option: 'Phần thưởng 2' },
        { option: 'Phần thưởng 3' },
        { option: 'Phần thưởng 4' },
        { option: 'Phần thưởng 5' },
      ]);
    } else {
      setSegments(newSegments);
    }
  };

  const handleAddSegment = () => {
    setSegments([...segments, { option: `Phần thưởng ${segments.length + 1}` }]);
  };

  const handleRemoveSegment = () => {
    if (segments.length > 2) {
      setSegments(segments.slice(0, -1));
    }
  };

  const handleSegmentNameChange = (index, newName) => {
    const newSegments = [...segments];
    newSegments[index] = { option: newName };
    setSegments(newSegments);
  };

  const handleResetHistory = () => {
    setWinningHistory([]);
    localStorage.removeItem('winningHistory');
  };

  const handleCloseWinningModal = () => {
    setShowWinningModal(false);
    setLastWonPrize('');
    // Tìm và dừng âm thanh chiến thắng
    const winSound = document.querySelector('audio[src*="win.mp3"]');
    if (winSound) {
      winSound.pause();
      winSound.currentTime = 0;
    }
  };

  const handleResetPrizes = () => {
    if (window.confirm('Bạn có chắc chắn muốn reset lại danh sách phần thưởng?')) {
      setSegments([
        { option: 'Phần thưởng 1' },
        { option: 'Phần thưởng 2' },
        { option: 'Phần thưởng 3' },
        { option: 'Phần thưởng 4' },
        { option: 'Phần thưởng 5' },
      ]);
    }
  };

  return (
    <AppWrapper>
      <Container>
        {/* prizeNumber = index phần thưởng (0-based); truyền trực tiếp để vòng quay và thông báo/xóa khớp nhau */}
        <WheelComponent
          mustSpin={mustSpin}
          prizeNumber={prizeNumber}
          segments={segments}
          onSpinClick={handleSpinClick}
          onStopSpinning={handleStopSpinning}
        />

        <WinningList
          winningHistory={winningHistory}
          onReset={handleResetHistory}
        />
      </Container>

      {showWinningModal && (
        <WinningModal
          prize={lastWonPrize}
          remainingPrizes={lastRemainingCount}
          onClose={handleCloseWinningModal}
        />
      )}

      <ConfigButton onClick={() => setShowConfigModal(true)}>
        ⚙️
      </ConfigButton>

      <ConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        segments={segments}
        onAddSegment={handleAddSegment}
        onRemoveSegment={handleRemoveSegment}
        onSegmentNameChange={handleSegmentNameChange}
        onResetPrizes={handleResetPrizes}
      />
    </AppWrapper>
  );
}

export default App;